import { useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { CompaniesService, Company } from 'api';
import { companiesAction, globalAction } from 'common/actions';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { useRefreshFirmCompanies } from 'services/hooks/firms';
import { useResponse } from 'services/hooks/useResponse';
import { extractResponseBodyMessage } from 'utilities';
import { UpdateCompanyParams, UseUpdateCompanyReturn } from './useUpdateCompany.types';

const DEFAULT_UPDATE_COMPANY_ERROR_MESSAGE = 'An error occurred while updating the Company';

const useUpdateCompany = (): UseUpdateCompanyReturn => {
  const [{ companyInfo }, dispatch] = useStore() as UseStoreValues;
  const { enqueueSnackbar } = useSnackbar();
  const { processErrorResponse } = useResponse();
  const { refreshFirmCompanies } = useRefreshFirmCompanies();

  const { mutateAsync } = useMutation({
    mutationFn: ({ companyId, companyData }: UpdateCompanyParams) =>
      CompaniesService.apiCompaniesUpdate(companyId, companyData as Company),
  });

  const updateCompany = useCallback(
    async (params: UpdateCompanyParams) => {
      dispatch(globalAction.showLoadingProgress(true));

      try {
        const updatedCompany = await mutateAsync(params);

        if (!updatedCompany?.id) {
          return null;
        }

        enqueueSnackbar(`${updatedCompany.name} successfully updated`, { variant: 'success' });
        dispatch(companiesAction.setCompanyInfo({ ...companyInfo, ...updatedCompany }));
        await refreshFirmCompanies({
          firmId: updatedCompany.firm ?? (companyInfo?.firm_id ? Number(companyInfo.firm_id) : undefined),
        });

        return updatedCompany;
      } catch (error: any) {
        // Show the reason a rejected request's body gives, such as a name clash; else the generic message.
        const status = error?.status ?? error?.response?.status;
        processErrorResponse({
          error,
          defaultErrorMessage: (status < 500 && extractResponseBodyMessage(error)) || DEFAULT_UPDATE_COMPANY_ERROR_MESSAGE,
          action: 'update the Company',
        });
        return null;
      } finally {
        dispatch(globalAction.showLoadingProgress(false));
      }
    },
    [companyInfo, dispatch, enqueueSnackbar, mutateAsync, processErrorResponse, refreshFirmCompanies]
  );

  return {
    updateCompany,
  };
};

export default useUpdateCompany;
